'use client';
import React, { useState } from 'react';
import { Building2, MapPin, Ruler, FileText, ShieldCheck, TrendingUp, ChevronDown, ChevronUp, Info, Home, BarChart3, AlertTriangle,  } from 'lucide-react';

// Backend integration point: replace with API call to /api/taxpayer/:tin/properties
const registeredProperties = [
  {
    id: 'prop-001',
    parcelId: 'MTC-2019-00841',
    propertyName: 'Kollie & Sons Commercial Complex',
    propertyType: 'Commercial — Retail & Wholesale',
    registrationStatus: 'Registered',
    registrationDate: '03/14/2019',
    address: 'Lot 22, Broad Street, Monrovia Central Business District',
    county: 'Montserrado',
    district: 'Greater Monrovia',
    zone: 'Zone A — Central Business District',
    circle: 'Circle 1 — Urban Core',
    landArea: '1,240 sq m',
    buildingArea: '3,680 sq m',
    floors: 4,
    yearBuilt: 2017,
    constructionType: 'Reinforced Concrete Frame',
    occupancyStatus: 'Owner-Occupied (Partial) + Tenanted',
    // Valuation
    marketValue: 485000,
    marketValueLRD: 77600000,
    selfDeclaredValue: 420000,
    selfDeclaredValueLRD: 67200000,
    assessedValue: 485000,
    assessedValueLRD: 77600000,
    valuationMethod: 'Comparative Market Analysis (CMA)',
    valuationDate: '01/15/2026',
    valuationCycle: 'FY 2026',
    valuationOfficer: 'Valuation Officer: J. Freeman (LRA-VAL-07)',
    // Tax
    taxClass: 'Class B — Commercial',
    taxRate: 1.5,
    annualTaxLiability: 7275,
    annualTaxLiabilityLRD: 1164000,
    exemptionStatus: 'None',
    // Ownership
    ownerName: 'Kollie & Sons Trading Company Ltd',
    ownerTIN: 'LR-0041820',
    ownershipType: 'Corporate Freehold',
    titleDeedNo: 'TD-MTC-2019-00841-A',
    titleVerified: true,
    encumbrances: 'None on record',
  },
  {
    id: 'prop-002',
    parcelId: 'MTC-2021-01204',
    propertyName: 'Kollie & Sons Warehouse Facility',
    propertyType: 'Industrial — Storage & Logistics',
    registrationStatus: 'Registered',
    registrationDate: '07/22/2021',
    address: 'Free Port Zone, Bushrod Island, Monrovia',
    county: 'Montserrado',
    district: 'Bushrod Island',
    zone: 'Zone C — Industrial / Port',
    circle: 'Circle 3 — Industrial Belt',
    landArea: '3,800 sq m',
    buildingArea: '2,950 sq m',
    floors: 1,
    yearBuilt: 2020,
    constructionType: 'Steel Frame & Corrugated Metal',
    occupancyStatus: 'Owner-Occupied',
    // Valuation
    marketValue: 210000,
    marketValueLRD: 33600000,
    selfDeclaredValue: 195000,
    selfDeclaredValueLRD: 31200000,
    assessedValue: 210000,
    assessedValueLRD: 33600000,
    valuationMethod: 'Cost Approach — Depreciated Replacement Cost',
    valuationDate: '02/08/2026',
    valuationCycle: 'FY 2026',
    valuationOfficer: 'Valuation Officer: A. Sumo (LRA-VAL-12)',
    // Tax
    taxClass: 'Class C — Industrial',
    taxRate: 1.2,
    annualTaxLiability: 2520,
    annualTaxLiabilityLRD: 403200,
    exemptionStatus: 'None',
    // Ownership
    ownerName: 'Kollie & Sons Trading Company Ltd',
    ownerTIN: 'LR-0041820',
    ownershipType: 'Corporate Freehold',
    titleDeedNo: 'TD-MTC-2021-01204-B',
    titleVerified: true,
    encumbrances: 'None on record',
  },
];

function ValueDiscrepancyBadge({ market, declared }: { market: number; declared: number }) {
  const diff = ((market - declared) / market) * 100;
  if (Math.abs(diff) < 3) return null;
  const over = diff > 0;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold"
      style={{
        backgroundColor: over ? 'rgba(251,146,60,0.15)' : 'rgba(74,222,128,0.12)',
        color: over ? '#fb923c' : '#4ade80',
        border: `1px solid ${over ? 'rgba(251,146,60,0.3)' : 'rgba(74,222,128,0.25)'}`,
      }}
    >
      <AlertTriangle size={10} />
      {over ? `Self-declared ${diff.toFixed(1)}% below market` : `Self-declared ${Math.abs(diff).toFixed(1)}% above market`}
    </span>
  );
}

function PropertyCard({ property, defaultOpen }: { property: typeof registeredProperties[0]; defaultOpen?: boolean }) {
  const [expanded, setExpanded] = useState(defaultOpen ?? false);

  const valuationVariance = ((property.marketValue - property.selfDeclaredValue) / property.marketValue) * 100;

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-200"
      style={{
        backgroundColor: 'var(--dark-card)',
        border: '1px solid var(--dark-border)',
      }}
    >
      {/* ── Property Identity Header ── */}
      <div
        className="px-5 py-4"
        style={{
          background: 'linear-gradient(135deg, rgba(184,134,11,0.12) 0%, rgba(212,160,23,0.06) 100%)',
          borderBottom: '1px solid var(--dark-border)',
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}
            >
              <Building2 size={18} style={{ color: '#fff' }} />
            </div>
            <div className="flex-1 min-w-0">
              {/* BOLD PROPERTY NAME — government grade */}
              <h3
                className="text-base font-extrabold leading-tight mb-0.5"
                style={{ color: 'var(--dark-text)', letterSpacing: '-0.01em' }}
              >
                {property.propertyName}
              </h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                <span
                  className="text-xs font-mono-data font-semibold px-2 py-0.5 rounded"
                  style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}
                >
                  Parcel ID: {property.parcelId}
                </span>
                <span
                  className="text-xs px-2 py-0.5 rounded-full font-semibold"
                  style={{ backgroundColor: 'rgba(74,222,128,0.12)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.25)' }}
                >
                  ✓ {property.registrationStatus}
                </span>
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                  {property.propertyType}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 text-xs" style={{ color: 'var(--dark-muted)' }}>
                <MapPin size={11} />
                <span>{property.address}</span>
              </div>
            </div>
          </div>

          {/* Tax class badge */}
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <span
              className="text-xs font-bold px-2.5 py-1 rounded-lg"
              style={{ backgroundColor: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.2)' }}
            >
              {property.taxClass}
            </span>
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 text-xs font-medium transition-all hover:opacity-80"
              style={{ color: 'var(--accent)' }}
            >
              {expanded ? 'Collapse' : 'Full Details'}
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
          </div>
        </div>
      </div>

      {/* ── Core Valuation Strip — always visible ── */}
      <div
        className="grid grid-cols-2 sm:grid-cols-4 divide-x"
        style={{ borderBottom: '1px solid var(--dark-border)', divideColor: 'var(--dark-border)' }}
      >
        {[
          {
            label: 'Market Value',
            value: `$${property.marketValue.toLocaleString()}`,
            sub: `LRD ${property.marketValueLRD.toLocaleString()}`,
            icon: <TrendingUp size={13} />,
            highlight: true,
            color: 'var(--accent)',
          },
          {
            label: 'Self-Declared Value',
            value: `$${property.selfDeclaredValue.toLocaleString()}`,
            sub: `LRD ${property.selfDeclaredValueLRD.toLocaleString()}`,
            icon: <FileText size={13} />,
            highlight: false,
            color: Math.abs(valuationVariance) > 3 ? '#fb923c' : '#4ade80',
          },
          {
            label: 'Assessed Value',
            value: `$${property.assessedValue.toLocaleString()}`,
            sub: `LRD ${property.assessedValueLRD.toLocaleString()}`,
            icon: <ShieldCheck size={13} />,
            highlight: false,
            color: 'var(--dark-text)',
          },
          {
            label: 'Annual Tax Liability',
            value: `$${property.annualTaxLiability.toLocaleString()}`,
            sub: `@ ${property.taxRate}% rate`,
            icon: <BarChart3 size={13} />,
            highlight: false,
            color: '#f87171',
          },
        ].map((item) => (
          <div key={item.label} className="px-4 py-3.5">
            <div className="flex items-center gap-1.5 mb-1" style={{ color: 'var(--dark-muted)' }}>
              {item.icon}
              <span className="text-xs font-medium uppercase tracking-wide" style={{ fontSize: '10px', letterSpacing: '0.06em' }}>
                {item.label}
              </span>
            </div>
            <p
              className="text-lg font-extrabold tabular-nums leading-tight"
              style={{ color: item.color }}
            >
              {item.value}
            </p>
            <p className="text-xs font-mono-data mt-0.5" style={{ color: 'var(--dark-muted)' }}>
              {item.sub}
            </p>
          </div>
        ))}
      </div>

      {/* Discrepancy warning */}
      {Math.abs(valuationVariance) > 3 && (
        <div
          className="px-5 py-2.5 flex items-center gap-2"
          style={{ backgroundColor: 'rgba(251,146,60,0.07)', borderBottom: '1px solid rgba(251,146,60,0.15)' }}
        >
          <AlertTriangle size={13} style={{ color: '#fb923c' }} />
          <p className="text-xs" style={{ color: '#fb923c' }}>
            <strong>Valuation Discrepancy Detected:</strong> Self-declared value is{' '}
            {valuationVariance.toFixed(1)}% below the LRA market assessment. This may trigger a valuation review.
          </p>
        </div>
      )}

      {/* ── Expanded Full Details ── */}
      {expanded && (
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {/* Location & Parcel */}
          <DetailSection
            title="Location & Parcel Details"
            icon={<MapPin size={14} />}
            items={[
              { label: 'County', value: property.county },
              { label: 'District', value: property.district },
              { label: 'Zone', value: property.zone },
              { label: 'LRA Circle', value: property.circle, bold: true },
              { label: 'Parcel ID', value: property.parcelId, mono: true },
              { label: 'Registration Date', value: property.registrationDate },
            ]}
          />

          {/* Physical Attributes */}
          <DetailSection
            title="Physical Attributes"
            icon={<Ruler size={14} />}
            items={[
              { label: 'Land Area', value: property.landArea, bold: true },
              { label: 'Building Area', value: property.buildingArea, bold: true },
              { label: 'No. of Floors', value: String(property.floors) },
              { label: 'Year Built', value: String(property.yearBuilt) },
              { label: 'Construction Type', value: property.constructionType },
              { label: 'Occupancy Status', value: property.occupancyStatus },
            ]}
          />

          {/* Valuation Details */}
          <DetailSection
            title="Valuation Details"
            icon={<TrendingUp size={14} />}
            items={[
              { label: 'Valuation Method', value: property.valuationMethod, bold: true },
              { label: 'Valuation Date', value: property.valuationDate },
              { label: 'Valuation Cycle', value: property.valuationCycle },
              { label: 'Valuation Officer', value: property.valuationOfficer },
              { label: 'Market Value (USD)', value: `$${property.marketValue.toLocaleString()}`, bold: true, highlight: true },
              { label: 'Self-Declared (USD)', value: `$${property.selfDeclaredValue.toLocaleString()}`, bold: true },
            ]}
          />

          {/* Tax Assessment */}
          <DetailSection
            title="Tax Assessment"
            icon={<BarChart3 size={14} />}
            items={[
              { label: 'Tax Class', value: property.taxClass, bold: true },
              { label: 'Applicable Rate', value: `${property.taxRate}% per annum`, bold: true, highlight: true },
              { label: 'Annual Liability (USD)', value: `$${property.annualTaxLiability.toLocaleString()}`, bold: true },
              { label: 'Annual Liability (LRD)', value: `LRD ${property.annualTaxLiabilityLRD.toLocaleString()}` },
              { label: 'Exemption Status', value: property.exemptionStatus },
              { label: 'Valuation Cycle', value: property.valuationCycle },
            ]}
          />

          {/* Ownership & Title */}
          <DetailSection
            title="Ownership & Title"
            icon={<ShieldCheck size={14} />}
            items={[
              { label: 'Registered Owner', value: property.ownerName, bold: true },
              { label: 'Owner TIN', value: property.ownerTIN, mono: true },
              { label: 'Ownership Type', value: property.ownershipType },
              { label: 'Title Deed No.', value: property.titleDeedNo, mono: true, bold: true },
              { label: 'Title Verification', value: property.titleVerified ? '✓ Verified by LRA' : 'Pending Verification', highlight: property.titleVerified },
              { label: 'Encumbrances', value: property.encumbrances },
            ]}
          />

          {/* Valuation Comparison */}
          <div
            className="rounded-xl p-4"
            style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={14} style={{ color: 'var(--accent)' }} />
              <h4 className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--dark-muted)', fontSize: '10px' }}>
                Valuation Comparison
              </h4>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Market Value', value: property.marketValue, color: 'var(--accent)', max: property.marketValue },
                { label: 'Self-Declared', value: property.selfDeclaredValue, color: Math.abs(valuationVariance) > 3 ? '#fb923c' : '#4ade80', max: property.marketValue },
                { label: 'Assessed Value', value: property.assessedValue, color: '#60a5fa', max: property.marketValue },
              ].map((bar) => (
                <div key={bar.label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: 'var(--dark-muted)' }}>{bar.label}</span>
                    <span className="font-bold tabular-nums" style={{ color: bar.color }}>
                      ${bar.value.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${(bar.value / bar.max) * 100}%`, backgroundColor: bar.color }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--dark-border)' }}>
              <ValueDiscrepancyBadge market={property.marketValue} declared={property.selfDeclaredValue} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DetailItem {
  label: string;
  value: string;
  bold?: boolean;
  mono?: boolean;
  highlight?: boolean;
}

function DetailSection({ title, icon, items }: { title: string; icon: React.ReactNode; items: DetailItem[] }) {
  return (
    <div
      className="rounded-xl p-4"
      style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}
    >
      <div className="flex items-center gap-2 mb-3">
        <span style={{ color: 'var(--accent)' }}>{icon}</span>
        <h4 className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--dark-muted)', fontSize: '10px' }}>
          {title}
        </h4>
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.label} className="flex items-start justify-between gap-2">
            <span className="text-xs flex-shrink-0" style={{ color: 'var(--dark-muted)', minWidth: '110px' }}>
              {item.label}
            </span>
            <span
              className={`text-xs text-right ${item.bold ? 'font-bold' : 'font-medium'} ${item.mono ? 'font-mono-data' : ''}`}
              style={{ color: item.highlight ? 'var(--accent)' : 'var(--dark-text)' }}
            >
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PropertyDetailsPanel() {
  return (
    <div className="mb-5">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}
          >
            <Home size={14} style={{ color: '#fff' }} />
          </div>
          <div>
            <h2 className="text-sm font-extrabold" style={{ color: 'var(--dark-text)' }}>
              Registered Properties
            </h2>
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
              {registeredProperties.length} propert{registeredProperties.length === 1 ? 'y' : 'ies'} on record · LRA Property Tax Registry
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="text-xs px-2.5 py-1 rounded-full font-semibold"
            style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.25)' }}
          >
            {registeredProperties.length} Active
          </span>
          <button
            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid var(--dark-border)', color: 'var(--dark-muted)' }}
          >
            <Info size={12} />
            Dispute Valuation
          </button>
        </div>
      </div>

      {/* Property cards */}
      <div className="space-y-3">
        {registeredProperties.map((prop, idx) => (
          <PropertyCard key={prop.id} property={prop} defaultOpen={idx === 0} />
        ))}
      </div>
    </div>
  );
}
